import { useRef } from 'preact/hooks';
import { isConvex } from '../../render/autoQuad';
import type { QuadPoints, Vec2 } from '../../render/homography';

const CORNER_NAMES = ['Far left corner', 'Far right corner', 'Near right corner', 'Near left corner'];

interface Props {
  quad: QuadPoints;
  onChange(q: QuadPoints): void;
}

/** Four draggable corner handles for the floor plane. Arrow keys nudge a focused handle. */
export function PerspectiveHandles({ quad, onChange }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const drag = useRef<number | null>(null);

  const move = (i: number, p: Vec2) => {
    const next = quad.map((q, k) => (k === i ? [Math.min(1, Math.max(0, p[0])), Math.min(1, Math.max(0, p[1]))] : q)) as QuadPoints;
    if (isConvex(next)) onChange(next);
  };

  const fromEvent = (e: PointerEvent): Vec2 => {
    const r = rootRef.current!.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  };

  return (
    <div
      ref={rootRef}
      class="absolute inset-0"
      style={{ touchAction: 'none' }}
      onPointerMove={(e) => drag.current !== null && move(drag.current, fromEvent(e))}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    >
      <svg class="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
        <polygon
          points={quad.map((p) => p.join(',')).join(' ')}
          fill="rgba(255,255,255,0.12)"
          stroke="white"
          stroke-width="2"
          stroke-dasharray="6 4"
          vector-effect="non-scaling-stroke"
        />
      </svg>
      {quad.map((p, i) => (
        <button
          key={i}
          type="button"
          aria-label={`${CORNER_NAMES[i]}. Use arrow keys to move.`}
          class="absolute grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-grab place-items-center"
          style={{ left: `${p[0] * 100}%`, top: `${p[1] * 100}%` }}
          onPointerDown={(e) => {
            e.preventDefault();
            drag.current = i;
            rootRef.current!.setPointerCapture(e.pointerId);
          }}
          onKeyDown={(e) => {
            const d = e.shiftKey ? 0.05 : 0.01;
            const delta: Record<string, Vec2> = { ArrowLeft: [-d, 0], ArrowRight: [d, 0], ArrowUp: [0, -d], ArrowDown: [0, d] };
            const v = delta[e.key];
            if (!v) return;
            e.preventDefault();
            move(i, [p[0] + v[0], p[1] + v[1]]);
          }}
        >
          <span class="block h-6 w-6 rounded-full border-[3px] border-white bg-brand shadow-lg" />
        </button>
      ))}
    </div>
  );
}
